/**
 * Identidade falsa do login de desenvolvimento — a parte pura, sem `window` e
 * sem `import.meta.env`, para poder ser testada (a fiação com o SDK falso fica
 * em `src/auth/mock.ts`, e a chave que liga tudo é `AUTH_MOCK`, em
 * `src/auth/config.ts`).
 *
 * O mock não fala com o Google: ele emite um "access token" que *carrega* o
 * perfil dentro de si, e o `fetchUserInfo` o abre em vez de chamar a
 * `oauth2/v3/userinfo`. É por isso que o prefixo abaixo é reconhecível a olho
 * nu — um token real do Google nunca começa assim, então não há como um
 * confundi-lo com o outro nem por acidente nem de propósito.
 */

import { emailDomain, normalizeEmail } from './emailAccess';

import type { GoogleUserInfo } from '../auth/types';

/** Marca do token falso. Serve de assinatura: sem ela, o token é do Google. */
export const MOCK_TOKEN_PREFIX = 'sicop-mock.';

/** Duração fingida do token, igual à do Google — 1h — para a sessão expirar igual. */
export const MOCK_TOKEN_EXPIRES_IN = 3600;

export function isMockAccessToken(accessToken: string): boolean {
  return accessToken.startsWith(MOCK_TOKEN_PREFIX);
}

/**
 * `encodeURIComponent` em vez de base64 porque nome de gente tem acento e o
 * `btoa` do navegador é latin-1 — "João" quebraria na hora de emitir o token.
 */
export function encodeMockAccessToken(profile: GoogleUserInfo): string {
  return MOCK_TOKEN_PREFIX + encodeURIComponent(JSON.stringify(profile));
}

/** O perfil de dentro do token, ou `null` quando o token não é do mock. */
export function decodeMockAccessToken(accessToken: string): GoogleUserInfo | null {
  if (!isMockAccessToken(accessToken)) return null;

  try {
    const profile = JSON.parse(
      decodeURIComponent(accessToken.slice(MOCK_TOKEN_PREFIX.length))
    ) as GoogleUserInfo;

    return profile?.email ? profile : null;
  } catch {
    return null;
  }
}

/**
 * "josue.henrique@gmail.com" → "Josue Henrique".
 *
 * O nome vem do e-mail porque é a única coisa que o mock tem: quem está
 * testando digita um endereço, não preenche um cadastro. Vale o suficiente
 * para a tela de Perfil, o avatar e a auditoria mostrarem algo de gente.
 */
export function displayNameFromEmail(email: string): string {
  const localPart = normalizeEmail(email).split('@')[0] ?? '';

  const name = localPart
    .split(/[._+-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

  return name || normalizeEmail(email);
}

/** O que o Google devolveria para esse e-mail, se o Google estivesse no meio. */
export function mockProfileFor(email: string): GoogleUserInfo {
  const normalized = normalizeEmail(email);

  return {
    email: normalized,
    email_verified: true,
    name: displayNameFromEmail(normalized),
    // Sem foto de propósito: o avatar cai nas iniciais, que é o caminho que
    // qualquer conta Google sem foto também segue.
  };
}

/**
 * O mock aceita **qualquer** e-mail bem formado — inclusive `@gmail.com`, que é o
 * ponto: em desenvolvimento não existe conta do domínio corporativo para
 * entrar. A única recusa é o que nem e-mail é, para o erro da tela ser
 * "digitou errado" e não um perfil sem endereço lá adiante.
 */
export function isMockEmail(email: string): boolean {
  return emailDomain(email) !== '';
}
